import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FileText, Image, Loader, ChevronDown, ChevronUp } from "lucide-react";
import DocumentPreviewModal from "../utils/DocumentPreviewModal";
import insurancePolicyService from "../../lib/InsurancePolicyService";
import { useAuth } from "../../hooks/useAuth";

const formatMoneyCents = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const cents = Number(value);
  if (Number.isNaN(cents)) return String(value);
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const vehicleCoverageSummary = (vehicle) => {
  if (!vehicle?.coverages?.length) return [];
  return vehicle.coverages.map((c) => {
    const parts = [];
    if (c?.name) parts.push(c.name);
    if (c?.per_person_limit_cents || c?.per_incident_limit_cents) {
      const perPerson = c?.per_person_limit_cents
        ? formatMoneyCents(c.per_person_limit_cents)
        : null;
      const perIncident = c?.per_incident_limit_cents
        ? formatMoneyCents(c.per_incident_limit_cents)
        : null;
      if (perPerson && perIncident) parts.push(`${perPerson} / ${perIncident}`);
      else if (perIncident) parts.push(`${perIncident} limit`);
      else if (perPerson) parts.push(`${perPerson} per person`);
    }
    if (c?.deductible_cents) {
      parts.push(`Deductible ${formatMoneyCents(c.deductible_cents)}`);
    }
    if (c?.declined) parts.push(`(${c.declined})`);
    return parts.join(" — ");
  });
};

const PolicyDetails = ({ checkin }) => {
  const { getIdToken } = useAuth();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!checkin?.id) return;
      try {
        setLoadingInsights(true);
        const token = await getIdToken();
        const response = await insurancePolicyService.getPolicyInsights(
          checkin.id,
          token
        );
        setInsights(response || null);
      } catch (err) {
        console.error("Failed to load insights", err);
        setInsights(null);
      } finally {
        setLoadingInsights(false);
      }
    };
    fetchInsights();
  }, [checkin?.id, getIdToken]);

  const parsedInsights = useMemo(() => {
    const item = insights || {};
    const parsed = { ...item };
    if (typeof parsed.document === "string") {
      try {
        parsed.document = JSON.parse(parsed.document);
      } catch (e) {
        console.error("Failed to parse insights.document", e);
      }
    }
    if (typeof parsed.approvalResult === "string") {
      try {
        parsed.approvalResult = JSON.parse(parsed.approvalResult);
      } catch (e) {
        console.error("Failed to parse insights.approvalResult", e);
      }
    }
    return parsed;
  }, [insights]);

  const extractedInsurance = useMemo(() => {
    const p = parsedInsights;
    if (!p?.document) return null;
    const doc = p.document;
    if (doc?.extracted_info) return doc.extracted_info;
    if (doc?.extractedInsurance) return doc.extractedInsurance;
    if (doc?.insurance) return doc.insurance;
    return null;
  }, [parsedInsights]);

  const primaryAddress = useMemo(() => {
    const ei = extractedInsurance;
    if (!ei?.addresses?.length) return null;
    const a = ei.addresses[0];
    if (a?.full_address) return a.full_address;
    return [a?.address_line_1, a?.address_line_2, a?.city, a?.state, a?.zip]
      .filter(Boolean)
      .join(", ") || null;
  }, [extractedInsurance]);

  const primaryInsuredName = useMemo(() => {
    const ei = extractedInsurance;
    if (!ei?.insured) return null;
    if (ei.insured?.name) return ei.insured.name;
    const drivers = ei.insured?.drivers;
    if (Array.isArray(drivers) && drivers.length) {
      const d = drivers[0];
      const parts = [d?.first_name, d?.last_name].filter(Boolean);
      if (parts.length) return parts.join(" ");
      if (d?.name) return d.name;
    }
    return null;
  }, [extractedInsurance]);

  const totalPremiumDollars = useMemo(() => {
    if (!extractedInsurance?.total_premium_cents) return null;
    return formatMoneyCents(extractedInsurance.total_premium_cents);
  }, [extractedInsurance]);

  const insightsLastUpdated = useMemo(() => {
    if (!checkin?.dateInsightsExtracted) return null;
    return new Date(checkin.dateInsightsExtracted).toLocaleString();
  }, [checkin]);

  const shouldShowInsights = Boolean(
    extractedInsurance || (insights && insights.id)
  );

  if (!checkin) return null;

  const statusBadgeClass =
    checkin.checkInStatus === "Confirmed"
      ? "bg-green-100 text-green-800"
      : checkin.checkInStatus === "Pending"
      ? "bg-yellow-100 text-yellow-800"
      : checkin.checkInStatus === "OnHold"
      ? "bg-orange-100 text-orange-800"
      : checkin.checkInStatus === "Declined"
      ? "bg-red-100 text-red-800"
      : "bg-gray-100 text-gray-800";

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-900">
            {checkin?.autoInsurance?.carrierName || "Insurance Policy"}
          </h3>
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass}`}
          >
            {checkin.checkInStatus || "Unknown"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {checkin.autoInsurance?.expiryDate && (
            <span className="text-sm text-gray-500">
              Expires{" "}
              {new Date(checkin.autoInsurance.expiryDate).toLocaleDateString()}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100 p-4 space-y-5">
          {/* Loading state for insights */}
          {loadingInsights && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader className="w-4 h-4 animate-spin" />
              Loading policy details...
            </div>
          )}

          {/* Declaration Summary */}
          {shouldShowInsights && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-lg font-bold text-gray-900">
                    Declaration Summary
                  </h4>
                  <p className="text-xs text-gray-500">
                    Extracted automatically from uploaded documents
                  </p>
                </div>
                {insightsLastUpdated && (
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Last extracted</div>
                    <div className="text-xs text-gray-500 italic">
                      {insightsLastUpdated}
                    </div>
                  </div>
                )}
              </div>

              {/* Policy Snapshot Grid */}
              {extractedInsurance && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Carrier
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">
                      {extractedInsurance.carrier_name || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Policy Number
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">
                      {extractedInsurance.carrier_policy_number || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Document Type
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">
                      {extractedInsurance.document_type || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Effective Date
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">
                      {extractedInsurance.effective_date || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Expiry Date
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">
                      {extractedInsurance.expiry_date || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Total Premium
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">
                      {totalPremiumDollars || "—"}
                    </div>
                  </div>
                </div>
              )}

              {/* Insured + Address */}
              {extractedInsurance && (primaryInsuredName || primaryAddress) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                  {primaryInsuredName && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-1">
                        Insured
                      </div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">
                        Name
                      </div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">
                        {primaryInsuredName}
                      </div>
                    </div>
                  )}
                  {primaryAddress && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-1">
                        Address
                      </div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">
                        Mailing / Residential
                      </div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">
                        {primaryAddress}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Drivers Table */}
              {extractedInsurance?.insured?.drivers?.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-gray-700 mb-2">
                    Drivers
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            Relationship
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            Date of Birth
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            License
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {extractedInsurance.insured.drivers.map((d, di) => {
                          const fullName =
                            [d?.first_name, d?.last_name]
                              .filter(Boolean)
                              .join(" ") ||
                            d?.name ||
                            "—";
                          return (
                            <tr
                              key={`driver-${di}`}
                              className="border-b border-gray-100"
                            >
                              <td className="px-3 py-2 text-gray-900">
                                {fullName}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {d?.relationship_to_insured || "—"}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {d?.date_of_birth || "—"}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {d?.drivers_license ||
                                  d?.license_number ||
                                  "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Vehicles Table */}
              {extractedInsurance?.vehicles?.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-semibold text-gray-700 mb-2">
                    Vehicles
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            Vehicle
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            VIN
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            Annual Mileage
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                            Key Coverages
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {extractedInsurance.vehicles.map((v, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-gray-100"
                          >
                            <td className="px-3 py-2 text-gray-900">
                              {[v?.year, v?.make, v?.model]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {v?.vin || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {v?.annual_mileage || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {v?.coverages?.length ? (
                                <div className="space-y-0.5">
                                  {vehicleCoverageSummary(v).map((c, ci) => (
                                    <div key={ci} className="text-xs">
                                      &bull; {c}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              {extractedInsurance?.rejection_reason && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mt-3">
                  <strong>Rejection reason:</strong>{" "}
                  {extractedInsurance.rejection_reason}
                </div>
              )}
            </div>
          )}

          {/* Document Files */}
          {checkin.files?.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                Policy Documents
              </div>
              {checkin.files.map((file, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-2 flex items-center justify-between"
                >
                  <div className="flex items-center">
                    {(file.documentType || file.type || "").includes("DEC") ? (
                      <FileText className="w-5 h-5 mr-2 text-blue-600" />
                    ) : (
                      <Image className="w-5 h-5 mr-2 text-green-600" />
                    )}
                    <span className="text-sm text-gray-700">
                      {file.url?.split("/").pop().split("_").pop() ||
                        "Insurance Document"}
                    </span>
                  </div>
                  <button
                    onClick={() => setPreviewUrl(file.url)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Preview
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <DocumentPreviewModal
        isOpen={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
        fileUrl={previewUrl}
      />
    </div>
  );
};

export default PolicyDetails;
